import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { supportedLocales } from '@/plugins/i18n'

// Locale code → flag-icons country code. Mirrors ada-app-ui's useLocale map
// (same name, same entries) — this is a modified variant of that composable:
// customer-ui's menu is flag-driven with locale.* name keys, so the
// machine-translated metadata and name building were not ported.
export const localeToIsoMap = Object.freeze({
  'en': 'gb',
  'sv': 'se',
  'am': 'et',
  'ar': 'sa',
  'az': 'az',
  'bg': 'bg',
  'bn': 'bd',
  'bs': 'ba',
  'ceb': 'ph',
  'cs': 'cz',
  'da': 'dk',
  'de': 'de',
  'el': 'gr',
  'es': 'es',
  'et': 'ee',
  'fa': 'ir',
  'fi': 'fi',
  'fr': 'fr',
  'hi': 'in',
  'hr': 'hr',
  'hu': 'hu',
  'hy': 'am',
  'id': 'id',
  'ig': 'ng',
  'is': 'is',
  'it': 'it',
  'ja': 'jp',
  'jw': 'id',
  'ka': 'ge',
  'kk': 'kz',
  'km': 'kh',
  'ko': 'kr',
  'ku': 'iq',
  'lt': 'lt',
  'lv': 'lv',
  'mg': 'mg',
  'mk': 'mk',
  'ml': 'in',
  'mr': 'in',
  'ms': 'my',
  'my': 'mm',
  'ne': 'np',
  'nl': 'nl',
  'no': 'no',
  'ny': 'mw',
  'pa': 'pk',
  'pl': 'pl',
  'ps': 'af',
  'pt': 'pt',
  'ro': 'ro',
  'ru': 'ru',
  'sd': 'pk',
  'si': 'lk',
  'sk': 'sk',
  'sl': 'sl',
  'so': 'so',
  'sq': 'al',
  'sr': 'sr',
  'su': 'id',
  'sw': 'ke',
  'ta': 'in',
  'te': 'in',
  'th': 'th',
  'tl': 'ph',
  'tr': 'tr',
  'uk': 'ua',
  'ur': 'pk',
  'uz': 'uz',
  'vi': 'vn',
  'yo': 'ng',
  'zh-cn': 'cn',
  // Traditional Chinese is the Taiwan locale — ada-app-ui's map still says 'cn'
  // (inherited legacy value); align it when this map is next touched there.
  'zh-tw': 'tw',
})

function flagFor(code: string) {
  return code in localeToIsoMap ? localeToIsoMap[code as keyof typeof localeToIsoMap] : code
}

// en/sv lead the menu, the rest follow alphabetically (matches the legacy order).
const priority = ['en', 'sv']

export function useLocale() {
  const { locale } = useI18n()

  // Derived from the actual locale bundles rather than a hardcoded list — the
  // menu can never offer a language whose file does not exist.
  const allLocales = supportedLocales
    .filter((code): code is string => typeof code === 'string')
    .map((code) => ({ code, flag: flagFor(code) }))
    .sort((a, b) => {
      const aIndex = priority.indexOf(a.code)
      const bIndex = priority.indexOf(b.code)
      if (aIndex !== -1 || bIndex !== -1) {
        return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex)
      }
      return a.code.localeCompare(b.code)
    })

  const currentFlag = computed(() => flagFor(locale.value))

  return { locale, allLocales, currentFlag }
}
